(() => {
    useWfmNavFactory.$inject = ['$state', '$filter', 'hUser', 'userPermissions', 'brandServices', 'harriSideBarService', 'useReportsNavFactory', 'useDashboardsNavFactory', 'useSettingsNav', 'employerPaySheetService', 'wfmVisibilityFactory'];

    function useWfmNavFactory($state, $filter, hUser, userPermissions, brandServices, harriSideBarService, useReportsNavFactory, useDashboardsNavFactory, useSettingsNav, employerPaySheetService, wfmVisibilityFactory) {
        return function ($scope, isActiveLink, getBrandIds) {
            const {reportsNav} = useReportsNavFactory($scope, isActiveLink, getBrandIds);
            const {wfmDashboardsNav} = useDashboardsNavFactory($scope, isActiveLink, getBrandIds);
            const {settingsNav} = useSettingsNav($scope);
            const {checkWFMNavVisibility} = wfmVisibilityFactory(getBrandIds);

            const wfmNavItemsCodes = {
                SCHEDULE: 'SCHEDULE',
                LIVEWIRE: 'LIVEWIRE',
                LIVEWIRE_V2: 'LIVEWIRE_V2',
                PUNCH_MANAGEMENT: 'PUNCH_MANAGEMENT',
                TIMESHEET: 'TIMESHEET',
            }

            const wfmNav = [{
                classNames: 'sb-main-items-container',
                search: {
                    enabled: true,
                    showInput: false,
                    term: ''
                },
                icon: {
                    unfilledSvg: '../images/svgs/teamlive.svg',
                    filledSvg: '../images/svgs/teamlive_filled.svg'
                },
                isVisible: async () => {
                    return await checkWFMNavVisibility();
                },  
                title: $filter('translate')('Workforce management'),
                sections: [
                    {
                        title: $filter('translate')('General'),
                        items: [
                            {
                                title: $filter('translate')('Team'),
                                icon: {
                                    unfilledSvg: '../images/svgs/team.svg',
                                    filledSvg: '../images/svgs/team_filled.svg'
                                },
                                isActive: () => isActiveLink('appLayout.schedule.core-hr.list', {source: 'TL'}),
                                href: () => $state.href('appLayout.schedule.core-hr.list', {source: 'TL'}),
                                automation: 'teamHeader'
                            },
                            {
                                title: $filter('translate')('Team check'),
                                icon: {
                                    unfilledSvg: '../images/svgs/team-check.svg',
                                    filledSvg: '../images/svgs/team-check_filled.svg'
                                },
                                isActive: () => isActiveLink('appLayout.schedule.team-health-check'),
                                href: () => $state.href('appLayout.schedule.team-health-check'),
                                automation: 'teamCheckLink',
                                isVisible: async () => {
                                    const activeBrandId = hUser.getActiveBrandIdWithAccess();
                                    const [
                                        isTeamQuestionnaireServiceEnabled,
                                        policyParser
                                    ] = await Promise.all([
                                        brandServices.check(brandServices.SERVICES.TEAM_QUESTIONING, activeBrandId),
                                        userPermissions.getBrandPolicy(activeBrandId)
                                    ]);

                                    return (
                                        isTeamQuestionnaireServiceEnabled &&
                                        policyParser.categories(policyParser.ANY).has(userPermissions.PERMISSIONS.MANAGE_EMPLOYEE_QUESTIONNAIRE)
                                    )
                                }
                            },
                            {
                                title: $filter('translate')('Schedule'),
                                icon: {
                                    unfilledSvg: '../images/svgs/schedule.svg',
                                    filledSvg: '../images/svgs/schedule_filled.svg'
                                },
                                isActive: () => isActiveLink('appLayout.schedule.main'),
                                href: () => $state.href('appLayout.schedule.main'),
                                code: wfmNavItemsCodes.SCHEDULE,
                                automation: 'teamHeaderScheduleLink',
                            },
                            {
                                title: $filter('translate')('Regular schedule variations'),
                                icon: {
                                    unfilledSvg: '../images/svgs/regular-schedule-variations.svg',
                                    filledSvg: '../images/svgs/regular-schedule-variations_filled.svg'
                                },
                                isActive: () => isActiveLink('appLayout.schedule.regular-schedule-variations'),
                                href: () => $state.href('appLayout.schedule.regular-schedule-variations'),
                                isVisible: async () => {
                                    const activeBrandId = hUser.getActiveBrandIdWithAccess();
                                    const regularScheduleSettings = await GoodFaithEstimateAPI.getRegularScheduleSettings(activeBrandId);
                                    return regularScheduleSettings.is_variations_enabled;
                                },
                            },
                            {
                                title: $filter('translate')('Schedule dash'),
                                icon: {
                                    unfilledSvg: '../images/svgs/schedule_dash.svg',
                                    filledSvg: '../images/svgs/schedule_dash_filled.svg'
                                },
                                isActive: () => isActiveLink('appLayout.schedule.schedule-dash'),
                                href: () => $state.href('appLayout.schedule.schedule-dash'),
                                isVisible: async () => {
                                    const activeBrandId = hUser.getActiveBrandIdWithAccess();
                                    const [
                                        policyParser,
                                        scheduleSettings
                                    ] = await Promise.all([
                                        userPermissions.getBrandPolicy(activeBrandId),
                                        TeamScheduleAPI.getScheduleSettings(activeBrandId),
                                    ])
                                    const canApproveSchedule = policyParser.has(userPermissions.PERMISSIONS.APPROVE_SCHEDULE);
                                    const approvalWorkflowEnabled = scheduleSettings.schedule_publish_mode === 'SUBMISSION';
                                    return canApproveSchedule && approvalWorkflowEnabled;
                                },
                            },
                            {
                                title: $filter('translate')('Livewire'),
                                icon: {
                                    unfilledSvg: '../images/svgs/livewire.svg',
                                    filledSvg: '../images/svgs/livewire_filled.svg'
                                },
                                code: wfmNavItemsCodes.LIVEWIRE,
                                isActive: () => isActiveLink('appLayout.schedule.performance'),
                                href: () => $state.href('appLayout.schedule.performance'),
                                isVisible: async () => {
                                    const brandIds = getBrandIds();
                                    const activeBrandId = hUser.getActiveBrandIdWithAccess();
                                    const [
                                        isLPMEnabled,
                                        policyParser,
                                        isLiveWireV2Enabled
                                    ] = await Promise.all([
                                        brandServices.check(brandServices.SERVICES.POS, activeBrandId, true),
                                        userPermissions.getBrandPolicy(activeBrandId),
                                        brandServices.check(brandServices.SERVICES.LIVE_WIRE_V2_DASHBOARD, brandIds[0])
                                    ]);

                                    const canViewLiveWireAndHarriLiveDashboards = policyParser.has(userPermissions.PERMISSIONS.VIEW_LIVE_WIRE_AND_HARRI_LIVE_DASHBOARDS);

                                    return (
                                        isLPMEnabled &&
                                        hUser.hasSalesAccess() &&
                                        canViewLiveWireAndHarriLiveDashboards &&
                                        !isLiveWireV2Enabled
                                    );
                                },
                            },
                            {
                                title: $filter('translate')('Livewire'),
                                icon: {
                                    unfilledSvg: '../images/svgs/livewire.svg',
                                    filledSvg: '../images/svgs/livewire_filled.svg'
                                },
                                code: wfmNavItemsCodes.LIVEWIRE_V2,
                                isActive: () => isActiveLink('appLayout.schedule.live-wire-v2'),
                                href: () => $state.href('appLayout.schedule.live-wire-v2'),
                                isVisible: async () => {
                                    const activeBrandId = hUser.getActiveBrandIdWithAccess();
                                    const [
                                        isLPMEnabled,
                                        policyParser,
                                        isLiveWireV2Enabled,
                                        isCarrolLaborServiceEnabled,
                                        isWendysLaborServiceEnabled,
                                        isABSServiceEnabled,
                                        isMcDBrandReportingEnabled,
                                    ] = await Promise.all([
                                        brandServices.check(brandServices.SERVICES.POS, activeBrandId, true),
                                        userPermissions.getBrandPolicy(activeBrandId),
                                        brandServices.check(brandServices.SERVICES.LIVE_WIRE_V2_DASHBOARD, activeBrandId),
                                        brandServices.check(brandServices.SERVICES.CARROL_LABOR_MODEL, activeBrandId),
                                        brandServices.check(brandServices.SERVICES.WENDYS_LABOR_MODEL, activeBrandId),
                                        brandServices.check(brandServices.SERVICES.ACTIVITY_BASED_SCHEDULING, activeBrandId),
                                        brandServices.check(brandServices.SERVICES.MCD_BRAND_REPORTING, activeBrandId),
                                    ]);

                                    const canViewLiveWireAndHarriLiveDashboards = policyParser.has(userPermissions.PERMISSIONS.VIEW_LIVE_WIRE_AND_HARRI_LIVE_DASHBOARDS);

                                    return (
                                        isLPMEnabled &&
                                        hUser.hasSalesAccess() &&
                                        canViewLiveWireAndHarriLiveDashboards &&
                                        isLiveWireV2Enabled &&
                                        (
                                            isCarrolLaborServiceEnabled ||
                                            isWendysLaborServiceEnabled ||
                                            isABSServiceEnabled ||
                                            isMcDBrandReportingEnabled
                                        )
                                    );
                                },
                            },
                            {
                                title: $filter('translate')('Budgeting'),
                                icon: {
                                    unfilledSvg: '../images/svgs/budget.svg',
                                    filledSvg: '../images/svgs/budget_filled.svg'
                                },
                                isActive: () => isActiveLink('appLayout.schedule.budgeting'),
                                href: () => $state.href('appLayout.schedule.budgeting'),
                                automation: 'teamHeaderBudgetingLink',
                                isVisible: async () => {
                                    const activeBrandId = hUser.getActiveBrandIdWithAccess();
                                    const [
                                        isBudgetingEnabled,
                                        policyParser
                                    ] = await Promise.all([
                                        brandServices.check(brandServices.SERVICES.SCHEDULE_BUDGETING, activeBrandId),
                                        userPermissions.getBrandPolicy(activeBrandId)
                                    ]);

                                    return (isBudgetingEnabled && policyParser.has(userPermissions.PERMISSIONS.MANAGE_BUDGET));
                                }
                            },
                            {
                                title: $filter('translate')('Reports'),
                                icon: {
                                    unfilledSvg: '../images/svgs/report.svg',
                                    filledSvg: '../images/svgs/report_filled.svg'
                                },
                                subNav: reportsNav,
                                isVisible: reportsNav[0].isVisible,
                            },
                            {
                                title: $filter('translate')('Dashboards'),
                                icon: {
                                    unfilledSvg: '../images/svgs/sh-dashboards.svg',
                                    filledSvg: '../images/svgs/sh-dashboards_filled.svg'
                                },
                                subNav: wfmDashboardsNav,
                                isVisible: wfmDashboardsNav[0].isVisible,
                            },
                        ]
                    },
                    {
                        title: $filter('translate')('Forecasting'),
                        isVisible: async () => {
                            return hUser.hasSalesAccess();
                        },
                        items: [
                            {
                                title: $filter('translate')('Forecasts and labor'),
                                icon: {
                                    unfilledSvg: '../images/svgs/forecasting.svg',
                                    filledSvg: '../images/svgs/forecasting_filled.svg'
                                },
                                automation: 'teamHeaderForecasting',
                                isActive: () => isActiveLink('appLayout.schedule.gastronomy-labor'),
                                href: () => $state.href('appLayout.schedule.gastronomy-labor'),
                                isVisible: async () => {
                                    const activeBrandId = hUser.getActiveBrandIdWithAccess();
                                    const [
                                        policyParser,
                                        isGastronomyLaborServiceEnabled
                                    ] = await Promise.all([
                                        userPermissions.getBrandPolicy(activeBrandId),
                                        brandServices.check(brandServices.SERVICES.GASTRONOMY_LABOR_MODEL, activeBrandId)
                                    ]);
                                    const hasManageSchedulePermission = policyParser.has(userPermissions.PERMISSIONS.MANAGE_SCHEDULE);

                                    return (
                                        hasManageSchedulePermission &&
                                        isGastronomyLaborServiceEnabled
                                    );
                                },
                            },
                            {
                                title: $filter('translate')('Forecasts and labor'),
                                icon: {
                                    unfilledSvg: '../images/svgs/forecasting.svg',
                                    filledSvg: '../images/svgs/forecasting_filled.svg'
                                },
                                automation: 'teamHeaderForecasting',
                                isActive: () => isActiveLink('appLayout.schedule.wendys-labor'),
                                href: () => $state.href('appLayout.schedule.wendys-labor'),
                                isVisible: async () => {
                                    const activeBrandId = hUser.getActiveBrandIdWithAccess();
                                    const [
                                        policyParser,
                                        isWendysLaborServiceEnabled
                                    ] = await Promise.all([
                                        userPermissions.getBrandPolicy(activeBrandId),
                                        brandServices.check(brandServices.SERVICES.WENDYS_LABOR_MODEL, activeBrandId)
                                    ]);
                                    const hasManageSchedulePermission = policyParser.has(userPermissions.PERMISSIONS.MANAGE_SCHEDULE);

                                    return (
                                        hasManageSchedulePermission &&
                                        isWendysLaborServiceEnabled
                                    );
                                },
                            },
                            {
                                title: $filter('translate')('Forecasts and labor'),
                                icon: {
                                    unfilledSvg: '../images/svgs/forecasting.svg',
                                    filledSvg: '../images/svgs/forecasting_filled.svg'
                                },
                                automation: 'teamHeaderForecasting',
                                isActive: () => isActiveLink('appLayout.schedule.carrol-labor'),
                                href: () => $state.href('appLayout.schedule.carrol-labor'),
                                isVisible: async () => {
                                    const activeBrandId = hUser.getActiveBrandIdWithAccess();
                                    const [
                                        policyParser,
                                        isCarrolLaborServiceEnabled
                                    ] = await Promise.all([
                                        userPermissions.getBrandPolicy(activeBrandId),
                                        brandServices.check(brandServices.SERVICES.CARROL_LABOR_MODEL, activeBrandId)
                                    ]);
                                    const hasManageSchedulePermission = policyParser.has(userPermissions.PERMISSIONS.MANAGE_SCHEDULE);

                                    return (
                                        hasManageSchedulePermission &&
                                        isCarrolLaborServiceEnabled
                                    );
                                },
                            },
                            {
                                title: $filter('translate')('Forecasts and labor'),
                                icon: {
                                    unfilledSvg: '../images/svgs/forecasting.svg',
                                    filledSvg: '../images/svgs/forecasting_filled.svg'
                                },
                                automation: 'teamHeaderForecasting',
                                isActive: () => isActiveLink('appLayout.schedule.mcd-labor'),
                                href: () => $state.href('appLayout.schedule.mcd-labor'),
                                isVisible: async () => {
                                    const activeBrandId = hUser.getActiveBrandIdWithAccess();
                                    const [
                                        policyParser,
                                        isMcDLaborServiceEnabled
                                    ] = await Promise.all([
                                        userPermissions.getBrandPolicy(activeBrandId),
                                        brandServices.check(brandServices.SERVICES.MCD_LABOR_MODEL, activeBrandId)
                                    ]);
                                    const hasManageSchedulePermission = policyParser.has(userPermissions.PERMISSIONS.MANAGE_SCHEDULE);

                                    return (
                                        hasManageSchedulePermission &&
                                        isMcDLaborServiceEnabled
                                    );
                                },
                            },
                            {
                                title: $filter('translate')('Forecasts and labor'),
                                icon: {
                                    unfilledSvg: '../images/svgs/forecasting.svg',
                                    filledSvg: '../images/svgs/forecasting_filled.svg'
                                },
                                automation: 'teamHeaderForecasting',
                                isActive: () => isActiveLink('appLayout.schedule.jib-labor'),
                                href: () => $state.href('appLayout.schedule.jib-labor.weeks-list'),
                                isVisible: async () => {
                                    const activeBrandId = hUser.getActiveBrandIdWithAccess();
                                    const [
                                        policyParser,
                                        isJibLaborServiceEnabled
                                    ] = await Promise.all([
                                        userPermissions.getBrandPolicy(activeBrandId),
                                        brandServices.check(brandServices.SERVICES.JACK_IN_THE_BOX_LABOR_MODEL, activeBrandId)
                                    ]);

                                    return (
                                        policyParser.has(userPermissions.PERMISSIONS.MANAGE_SCHEDULE) &&
                                        isJibLaborServiceEnabled
                                    );
                                },
                            },
                            {
                                title: $filter('translate')('Forecasts and labor'),
                                icon: {
                                    unfilledSvg: '../images/svgs/forecasting.svg',
                                    filledSvg: '../images/svgs/forecasting_filled.svg'
                                },
                                automation: 'teamHeaderForecasting',
                                isActive: () => isActiveLink('appLayout.schedule.forecasts-and-labor'),
                                href: () => $state.href('appLayout.schedule.forecasts-and-labor'),
                                isVisible: async () => {
                                    const activeBrandId = hUser.getActiveBrandIdWithAccess();
                                    const [
                                        policyParser,
                                        isABSServiceEnabled
                                    ] = await Promise.all([
                                        userPermissions.getBrandPolicy(activeBrandId),
                                        brandServices.check(brandServices.SERVICES.ACTIVITY_BASED_SCHEDULING, activeBrandId)
                                    ]);
                                    const hasManageSchedulePermission = policyParser.has(userPermissions.PERMISSIONS.MANAGE_SCHEDULE);

                                    return (
                                        hasManageSchedulePermission &&
                                        isABSServiceEnabled
                                    );
                                },
                            },
                            {
                                title: $filter('translate')('Forecasts'),
                                icon: {
                                    unfilledSvg: '../images/svgs/forecasting.svg',
                                    filledSvg: '../images/svgs/forecasting_filled.svg'
                                },
                                automation: 'teamHeaderForecasting',
                                isActive: () => isActiveLink('appLayout.schedule.forecasts-and-labor'),
                                href: () => $state.href('appLayout.schedule.forecasts-and-labor'),
                                isVisible: async () => {
                                    const activeBrandId = hUser.getActiveBrandIdWithAccess();
                                    const [
                                        policyParser,
                                        isDemandForecastingEnabled,
                                        isGastronomyLaborServiceEnabled,
                                        isWendysLaborServiceEnabled,
                                        isCarrolLaborServiceEnabled,
                                        isMcDLaborServiceEnabled,
                                        isJibLaborServiceEnabled,
                                        isABSServiceEnabled,
                                    ] = await Promise.all([
                                        userPermissions.getBrandPolicy(activeBrandId),
                                        brandServices.check(brandServices.SERVICES.DEMAND_FORECASTING, activeBrandId),
                                        brandServices.check(brandServices.SERVICES.GASTRONOMY_LABOR_MODEL, activeBrandId),
                                        brandServices.check(brandServices.SERVICES.WENDYS_LABOR_MODEL, activeBrandId),
                                        brandServices.check(brandServices.SERVICES.CARROL_LABOR_MODEL, activeBrandId),
                                        brandServices.check(brandServices.SERVICES.MCD_LABOR_MODEL, activeBrandId),
                                        brandServices.check(brandServices.SERVICES.JACK_IN_THE_BOX_LABOR_MODEL, activeBrandId),
                                        brandServices.check(brandServices.SERVICES.ACTIVITY_BASED_SCHEDULING, activeBrandId)
                                    ]);
                                    const hasManageSchedulePermission = policyParser.has(userPermissions.PERMISSIONS.MANAGE_SCHEDULE);

                                    return (
                                        hasManageSchedulePermission &&
                                        isDemandForecastingEnabled &&
                                        !
                                            (
                                                isGastronomyLaborServiceEnabled ||
                                                isWendysLaborServiceEnabled ||
                                                isCarrolLaborServiceEnabled ||
                                                isMcDLaborServiceEnabled ||
                                                isJibLaborServiceEnabled ||
                                                isABSServiceEnabled
                                            )
                                    );
                                },
                            },
                            {
                                title: $filter('translate')('Historical data'),
                                icon: {
                                    unfilledSvg: '../images/svgs/historical_data.svg',
                                    filledSvg: '../images/svgs/historical_data_filled.svg'
                                },
                                isActive: () => isActiveLink('appLayout.schedule.forecasts-historical-data'),
                                href: () => $state.href('appLayout.schedule.forecasts-historical-data'),
                                isVisible: async () => {
                                    const activeBrandId = hUser.getActiveBrandIdWithAccess();
                                    const [
                                        isDemandForecastingEnabled,
                                        policyParser
                                    ] = await Promise.all([
                                        brandServices.check(brandServices.SERVICES.DEMAND_FORECASTING, activeBrandId),
                                        userPermissions.getBrandPolicy(activeBrandId),
                                    ]);
                                    const hasManageSchedulePermission = policyParser.has(userPermissions.PERMISSIONS.MANAGE_SCHEDULE);
                                    return (
                                        isDemandForecastingEnabled &&
                                        hasManageSchedulePermission
                                    )
                                },
                            },
                            {
                                title: $filter('translate')('Par labor'),
                                icon: {
                                    unfilledSvg: '../images/svgs/par_labor.svg',
                                    filledSvg: '../images/svgs/par_labor_filled.svg'
                                },
                                isActive: () => isActiveLink('appLayout.schedule.par-labor'),
                                href: () => $state.href('appLayout.schedule.par-labor'),
                                isVisible: async () => {
                                    const activeBrandId = hUser.getActiveBrandIdWithAccess();
                                    return await brandServices.check(brandServices.SERVICES.PAR_LABOR, activeBrandId);
                                },
                            }
                        ]
                    },
                    {
                        title: $filter('translate')('TeamHub'),
                        isVisible: async () => {
                            const activeBrandId = hUser.getActiveBrandIdWithAccess();
                            return await brandServices.check(brandServices.SERVICES.HARRI_CLOCK, activeBrandId);
                        },
                        items: [
                            {
                                title: $filter('translate')('Punch management'),
                                icon: {
                                    unfilledSvg: '../images/svgs/punch_management.svg',
                                    filledSvg: '../images/svgs/punch_management_filled.svg'
                                },
                                code: wfmNavItemsCodes.PUNCH_MANAGEMENT,
                                isActive: () => isActiveLink('appLayout.schedule.punch-management'),
                                href: () => $state.href('appLayout.schedule.punch-management'),
                                isVisible: async () => {
                                    const activeBrandId = hUser.getActiveBrandIdWithAccess();
                                    const [
                                        isClockTimeAppEnabled,
                                        policyParser
                                    ] = await Promise.all([
                                        brandServices.check(brandServices.SERVICES.HARRI_CLOCK, activeBrandId),
                                        userPermissions.getBrandPolicy(activeBrandId)
                                    ]);
                                    const canManagePunchManagement = policyParser.has(userPermissions.PERMISSIONS.CAN_MANAGE_USER_PUNCHES);
                                    return isClockTimeAppEnabled && canManagePunchManagement;
                                },
                            },
                            {
                                title: $filter('translate')('Timesheet'),
                                icon: {
                                    unfilledSvg: '../images/svgs/timesheet.svg',
                                    filledSvg: '../images/svgs/timesheet_filled.svg'
                                },
                                code: wfmNavItemsCodes.TIMESHEET,
                                isActive: () => isActiveLink('appLayout.schedule.clock-time.timesheet'),
                                href: () => $state.href('appLayout.schedule.clock-time.timesheet'),
                                isVisible: async () => {
                                    const activeBrandId = hUser.getActiveBrandIdWithAccess();
                                    const [
                                        isClockTimeAppEnabled,
                                        policyParser
                                    ] = await Promise.all([
                                        brandServices.check(brandServices.SERVICES.HARRI_CLOCK, activeBrandId),
                                        userPermissions.getBrandPolicy(activeBrandId)
                                    ]);
                                    const isAllowedAccessTimeSheet = policyParser.hasAny(userPermissions.MANAGE_TIME_CARDS);
                                    return isClockTimeAppEnabled && isAllowedAccessTimeSheet;
                                },
                            },
                            {
                                title: $filter('translate')('Pay runs'),
                                icon: {
                                    unfilledSvg: '../images/svgs/pay-runs.svg',
                                    filledSvg: '../images/svgs/pay-runs_filled.svg'
                                },
                                isActive: () => isActiveLink('appLayout.schedule.clock-time.pay-runs'),
                                href: () => $state.href('appLayout.schedule.clock-time.pay-runs'),
                                isVisible: async () => {
                                    const activeBrandId = hUser.getActiveBrandIdWithAccess();
                                    let canManagePayRuns = false;
                                    const [
                                        isClockTimeAppEnabled,
                                        policyParser
                                    ] = await Promise.all([
                                        brandServices.check(brandServices.SERVICES.HARRI_CLOCK, activeBrandId),
                                        userPermissions.getBrandPolicy(activeBrandId)
                                    ]);
                                    const hasManagePayRunsAccess = policyParser.has(userPermissions.PERMISSIONS.MANAGE_PAYSHEET_PAYRUN);

                                    if (hasManagePayRunsAccess) {
                                        const brandPayrunsTree = await PerformanceAggregatorAPI.getBrandPayrunsTree(activeBrandId);
                                        canManagePayRuns = !!brandPayrunsTree?.payruns_tree?.find((tree) => tree.brand_id === activeBrandId);
                                    }
                                    return isClockTimeAppEnabled && canManagePayRuns;
                                },
                            },
                            {
                                title: $filter('translate')('Pay data sheet'),
                                icon: {
                                    unfilledSvg: '../images/svgs/paydata_sheet.svg',
                                    filledSvg: '../images/svgs/paydata_sheet_filled.svg'
                                },
                                isActive: () => isActiveLink('appLayout.schedule.clock-time.pay-sheet'),
                                href: () => $state.href('appLayout.schedule.clock-time.pay-sheet'),
                                isVisible: async () => {
                                    const activeBrandId = hUser.getActiveBrandIdWithAccess();
                                    const [
                                        isClockTimeAppEnabled,
                                        policyParser,
                                        teamLiveBrandsList
                                    ] = await Promise.all([
                                        brandServices.check(brandServices.SERVICES.HARRI_CLOCK, activeBrandId),
                                        userPermissions.getBrandPolicy(activeBrandId),
                                        GatewayTeamLiveAPI.getBrandsSettingsV3(hUser.getUserContext().brand_id, {brand_ids: [activeBrandId]})
                                    ]);
                                    const isPaySheetAdmin = policyParser.has(userPermissions.PERMISSIONS.ADVANCED_PDS_ACCESS);
                                    const brandSettings = teamLiveBrandsList.find((brand) => {
                                        return brand.id === activeBrandId;
                                    });
                                    const showPayDataSheetTab = await employerPaySheetService.canShowPayDataSheetTab(brandSettings);
                                    return isClockTimeAppEnabled && isPaySheetAdmin && showPayDataSheetTab;
                                },
                            },
                            {
                                title: $filter('translate')('Regular schedule incidents'),
                                icon: {
                                    unfilledSvg: '../images/svgs/regular_schedule.svg',
                                    filledSvg: '../images/svgs/regular_schedule_filled.svg'
                                },
                                isActive: () => isActiveLink('appLayout.schedule.clock-time.gfe-incidents'),
                                href: () => $state.href('appLayout.schedule.clock-time.gfe-incidents'),
                                isVisible: async () => {
                                    const activeBrandId = hUser.getActiveBrandIdWithAccess();
                                    let gfeBrandSettings;
                                    const [
                                        isGFEServiceEnabled,
                                        policyParser
                                    ] = await Promise.all([
                                        brandServices.check(brandServices.SERVICES.GFE_INCIDENTS_TRACKING, activeBrandId),
                                        userPermissions.getBrandPolicy(activeBrandId),
                                    ]);

                                    if (isGFEServiceEnabled) {
                                        gfeBrandSettings = await GoodFaithEstimateAPI.getGFEBrandSettings(activeBrandId);
                                    }
                                    const canManageGFEIncidents = policyParser.has(userPermissions.PERMISSIONS.MANAGE_GFE_INCIDENTS);

                                    return (
                                        isGFEServiceEnabled &&
                                        gfeBrandSettings?.settings?.state &&
                                        gfeBrandSettings?.settings?.state === 'NY' &&
                                        canManageGFEIncidents
                                    );
                                },
                            },
                            {
                                title: $filter('translate')('GFE incidents'),
                                icon: {
                                    unfilledSvg: '../images/svgs/gfe.svg',
                                    filledSvg: '../images/svgs/gfe_filled.svg'
                                },
                                isActive: () => isActiveLink('appLayout.schedule.clock-time.gfe-incidents'),
                                href: () => $state.href('appLayout.schedule.clock-time.gfe-incidents'),
                                isVisible: async () => {
                                    const activeBrandId = hUser.getActiveBrandIdWithAccess();
                                    let gfeBrandSettings;
                                    const [
                                        isGFEServiceEnabled,
                                        policyParser
                                    ] = await Promise.all([
                                        brandServices.check(brandServices.SERVICES.GFE_INCIDENTS_TRACKING, activeBrandId),
                                        userPermissions.getBrandPolicy(activeBrandId),
                                    ]);

                                    if (isGFEServiceEnabled) {
                                        gfeBrandSettings = await GoodFaithEstimateAPI.getGFEBrandSettings(activeBrandId);
                                    }
                                    const canManageGFEIncidents = policyParser.has(userPermissions.PERMISSIONS.MANAGE_GFE_INCIDENTS);

                                    return (
                                        isGFEServiceEnabled &&
                                        gfeBrandSettings?.settings?.state &&
                                        gfeBrandSettings?.settings?.state !== 'NY' &&
                                        canManageGFEIncidents
                                    );
                                },
                            },
                            {
                                title: $filter('translate')('Announcements'),
                                icon: {
                                    unfilledSvg: '../images/svgs/announcement.svg',
                                    filledSvg: '../images/svgs/announcement_filled.svg'
                                },
                                isActive: () => isActiveLink('appLayout.schedule.clock-time.announcements'),
                                href: () => $state.href('appLayout.schedule.clock-time.announcements'),
                            },
                            {
                                title: $filter('translate')('Feedback'),
                                icon: {
                                    unfilledSvg: '../images/svgs/feedback.svg',
                                    filledSvg: '../images/svgs/feedback_filled.svg'
                                },
                                isActive: () => isActiveLink('appLayout.schedule.clock-time.feedback'),
                                href: () => $state.href('appLayout.schedule.clock-time.feedback'),
                            },
                            {
                                title: $filter('translate')('Alerts'),
                                icon: {
                                    unfilledSvg: '../images/svgs/alert.svg',
                                    filledSvg: '../images/svgs/alert_filled.svg'
                                },
                                isActive: () => isActiveLink('appLayout.schedule.clock-time.alerts'),
                                href: () => $state.href('appLayout.schedule.clock-time.alerts'),
                            },
                        ]
                    },
                    {
                        title: $filter('translate')('Requests'),
                        items: [
                            {
                                title: $filter('translate')('Shift trade'),
                                icon: {
                                    unfilledSvg: '../images/svgs/shift_trade.svg',
                                    filledSvg: '../images/svgs/shift_trade_filled.svg'
                                },
                                isActive: () => isActiveLink('appLayout.schedule.shift-trading'),
                                href: () => $state.href('appLayout.schedule.shift-trading'),
                            },
                            {
                                title: $filter('translate')('Availability'),
                                icon: {
                                    unfilledSvg: '../images/svgs/availability.svg',
                                    filledSvg: '../images/svgs/availability_filled.svg'
                                },
                                isActive: () => isActiveLink('appLayout.schedule.availability-requests'),
                                href: () => $state.href('appLayout.schedule.availability-requests'),
                                isVisible: async () => {
                                    const activeBrandId = hUser.getActiveBrandIdWithAccess();
                                    const policyParser = await userPermissions.getBrandPolicy(activeBrandId);
                                    return policyParser.has(userPermissions.PERMISSIONS.MANAGE_AVAILABILITY_REQUESTS);
                                },
                            },
                            {
                                title: $filter('translate')('Time off'),
                                icon: {
                                    unfilledSvg: '../images/svgs/time_off.svg',
                                    filledSvg: '../images/svgs/time_off_filled.svg'
                                },
                                isActive: () => isActiveLink('appLayout.schedule.time-off'),
                                href: () => $state.href('appLayout.schedule.time-off'),
                                isVisible: async () => {
                                    const activeBrandId = hUser.getActiveBrandIdWithAccess();
                                    const [
                                        policyParser,
                                        teamLiveBrandsList
                                    ] = await Promise.all([
                                        userPermissions.getBrandPolicy(activeBrandId),
                                        GatewayTeamLiveAPI.getBrandsSettingsV3(hUser.getUserContext().brand_id, {brand_ids: [activeBrandId]})
                                    ]);
                                    const brandSettings = teamLiveBrandsList.find((brand) => {
                                        return brand.id === activeBrandId;
                                    });
                                    const isTimeOffShown = await harriSideBarService.isAbsenceTypeTabShown(brandSettings, 'TIME_OFF');
                                    const hasManageTimeOffRequestsPermission = policyParser.has(userPermissions.PERMISSIONS.MANAGE_TIMEOFF_REQUESTS);
                                    return (isTimeOffShown && hasManageTimeOffRequestsPermission);
                                },
                            },
                            {
                                title: $filter('translate')('Absence'),
                                icon: {
                                    unfilledSvg: '../images/svgs/abscense.svg',
                                    filledSvg: '../images/svgs/absence_filled.svg'
                                },
                                isActive: () => isActiveLink('appLayout.schedule.absence-requests'),
                                href: () => $state.href('appLayout.schedule.absence-requests'),
                                isVisible: async () => {
                                    const activeBrandId = hUser.getActiveBrandIdWithAccess();
                                    const [
                                        policyParser,
                                        teamLiveBrandsList
                                    ] = await Promise.all([
                                        userPermissions.getBrandPolicy(activeBrandId),
                                        GatewayTeamLiveAPI.getBrandsSettingsV3(hUser.getUserContext().brand_id, {brand_ids: [activeBrandId]})
                                    ]);
                                    const brandSettings = teamLiveBrandsList.find((brand) => {
                                        return brand.id === activeBrandId;
                                    });
                                    const isAbsenceRequestsShown = harriSideBarService.canShowAbsenceRequests(brandSettings);
                                    const hasManageAbsenceRequestsPermission = policyParser.has(userPermissions.PERMISSIONS.MANAGE_ABSENCE_REQUESTS);
                                    return (hasManageAbsenceRequestsPermission && isAbsenceRequestsShown);
                                },
                            },
                            {
                                title: $filter('translate')('Sickness'),
                                icon: {
                                    unfilledSvg: '../images/svgs/sickness.svg',
                                    filledSvg: '../images/svgs/sickness_filled.svg'
                                },
                                isActive: () => isActiveLink('appLayout.schedule.sickness-requests'),
                                href: () => $state.href('appLayout.schedule.sickness-requests'),
                                isVisible: async () => {
                                    const activeBrandId = hUser.getActiveBrandIdWithAccess();
                                    const [
                                        policyParser,
                                        teamLiveBrandsList
                                    ] = await Promise.all([
                                        userPermissions.getBrandPolicy(activeBrandId),
                                        GatewayTeamLiveAPI.getBrandsSettingsV3(hUser.getUserContext().brand_id, {brand_ids: [activeBrandId]})
                                    ]);
                                    const brandSettings = teamLiveBrandsList.find((brand) => {
                                        return brand.id === activeBrandId;
                                    });
                                    const isSicknessShown = harriSideBarService.isAbsenceTypeTabShown(brandSettings, 'SICKNESS');
                                    const hasManageSicknessRequestsPermission = policyParser.has(userPermissions.PERMISSIONS.MANAGE_SICKNESS_REQUESTS);
                                    return (hasManageSicknessRequestsPermission && isSicknessShown);
                                },
                            },
                            {
                                title: $filter('translate')('Team requests'),
                                icon: {
                                    unfilledSvg: '../images/svgs/team_requests.svg',
                                    filledSvg: '../images/svgs/team_requests_filled.svg'
                                },
                                isActive: () => isActiveLink('appLayout.schedule.approved-requests'),
                                href: () => $state.href('appLayout.schedule.approved-requests'),
                            },
                            {
                                title: $filter('translate')('Employee consents'),
                                icon: {
                                    unfilledSvg: '../images/svgs/employee_consent.svg',
                                    filledSvg: '../images/svgs/employee_consent_filled.svg'
                                },
                                isActive: () => isActiveLink('appLayout.schedule.schedule-change-consent'),
                                href: () => $state.href('appLayout.schedule.schedule-change-consent'),
                                isVisible: async () => {
                                    const activeBrandId = hUser.getActiveBrandIdWithAccess();
                                    const scheduleSettings = await TeamScheduleAPI.getScheduleSettings(activeBrandId);
                                    return scheduleSettings.fair_work_week_settings?.penalty_general_settings?.employee_consent_flow_enabled;
                                },
                            },
                        ]
                    },
                    {
                        title: $filter('translate')('Logbook'),
                        isVisible: async () => {
                            const activeBrandId = hUser.getActiveBrandIdWithAccess();
                            return await brandServices.check(brandServices.SERVICES.LOGBOOK, activeBrandId);
                        },
                        items: [
                            {
                                title: $filter('translate')('Logbook dashboard'),
                                icon: {
                                    unfilledSvg: '../images/svgs/logbook-dashboard.svg',
                                    filledSvg: '../images/svgs/logbook-dashboard_filled.svg'
                                },
                                isActive: () => isActiveLink('appLayout.schedule.logbook.dashboard'),
                                href: () => $state.href('appLayout.schedule.logbook.dashboard'),
                                isVisible: async () => true,
                            },
                            {
                                title: $filter('translate')('Entries'),
                                icon: {
                                    unfilledSvg: '../images/svgs/entries.svg',
                                    filledSvg: '../images/svgs/entries_filled.svg'
                                },
                                isActive: () => isActiveLink('appLayout.schedule.logbook.logbook-entries'),
                                href: () => $state.href('appLayout.schedule.logbook.logbook-entries'),
                                isVisible: async () => {
                                    const activeBrandId = hUser.getActiveBrandIdWithAccess();
                                    const logbookAccess = await LogbookAPI.getLogbookActions(activeBrandId);
                                    return logbookAccess.view_entries;
                                }
                            },
                            {
                                title: $filter('translate')('Checklists'),
                                icon: {
                                    unfilledSvg: '../images/svgs/checklists.svg',
                                    filledSvg: '../images/svgs/checklists_filled.svg'
                                },
                                isActive: () => isActiveLink('appLayout.schedule.logbook.logbook-checklists'),
                                href: () => $state.href('appLayout.schedule.logbook.logbook-checklists'),
                                isVisible: async () => true
                            },
                            {
                                title: $filter('translate')('My tasks'),
                                icon: {
                                    unfilledSvg: '../images/svgs/my_tasks.svg',
                                    filledSvg: '../images/svgs/my_tasks_filled.svg'
                                },
                                isActive: () => $state.is('appLayout.schedule.logbook.logbook-team-tasks', {'my-tasks': true}),
                                href: () => $state.href('appLayout.schedule.logbook.logbook-team-tasks', {'my-tasks': true}, {inherit: false}),
                                isVisible: async () => {
                                    const activeBrandId = hUser.getActiveBrandIdWithAccess();
                                    const userLocations = await TeamAPI.getEmployeeLocations(activeBrandId) ?? [];
                                    const filteredLocations = userLocations.filter((location) => location.services.includes(brandServices.SERVICES.LOGBOOK));
                                    return filteredLocations.length > 0;
                                },
                            },
                            {
                                title: $filter('translate')('Tasks'),
                                icon: {
                                    unfilledSvg: '../images/svgs/team_tasks.svg',
                                    filledSvg: '../images/svgs/team_tasks_filled.svg'
                                },
                                isActive: () => $state.is('appLayout.schedule.logbook.logbook-team-tasks', {'my-tasks': false}),
                                href: () => $state.href('appLayout.schedule.logbook.logbook-team-tasks', {'my-tasks': false}, {inherit: false}),
                                isVisible: async () => true,
                            },
                        ]
                    },
                    {
                        items: [
                            {
                                title: $filter('translate')('Settings'),
                                icon: {
                                    unfilledSvg: '../images/svgs/settings.svg',
                                    filledSvg: '../images/svgs/settings_filled.svg'
                                },
                                isVisible: async () => {
                                    const policyParser = await userPermissions.getBrandPolicy(hUser.getActiveBrandIdWithAccess());

                                    if (
                                        !hUser.isHMAPUser() &&
                                        policyParser.hasAccess(userPermissions.ACCESS_TYPES.TEAM) &&
                                        !policyParser.hasAccess(userPermissions.ACCESS_TYPES.SOURCE_HIRE)
                                    ) {
                                        return await settingsNav[0].isVisible();
                                    }

                                    return false;
                                },
                                subNav: settingsNav
                            },
                        ]
                    },
                ],
            }];

            return {
                wfmNav,
                wfmNavItemsCodes
            }
        }
    }

    angular
        .module("harriApp")
        .factory('useWfmNavFactory', useWfmNavFactory);
})();